import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  BadgeCheck,
  Check,
  ChevronRight,
  Home,
  Languages,
  MapPin,
  Search,
  ShieldCheck,
  ShoppingCart,
  Store,
  Users,
  Wrench,
} from "lucide-react";
import { AsLogo, AsWordmark } from "@/components/AsLogo";
import { cityInitial, CityWordmark, useCityOnline } from "@/components/CityBrand";
import { TENANT_KEY } from "@/lib/api";
import { useApp } from "@/state/AppContext";
import { localized } from "@/theme/tenantTheme";

/** The explicit-language marker. Deliberately NOT `AppContext`'s own key: that one always has a
 *  value (it falls back to `en`), so it can say which language to render but never whether the
 *  visitor actually chose one. This key exists only to answer "have they been asked yet". */
const LANG_CHOICE_KEY = "as_lang";
/** Last-good city list, so a repeat visitor on a dead line still sees their town while Retry runs. */
const CITY_CACHE_KEY = "as_last_cities";

const SUPPORT = "support@apnashahar.online";
/** Prefilled, because a low-confidence user facing a blank email usually just closes it. */
const ADD_CITY_MAILTO =
  `mailto:${SUPPORT}?subject=${encodeURIComponent("Add my town")}` +
  `&body=${encodeURIComponent("Town name:\nState:\nYour name:\n")}`;
/** Operators write in the same way — there is no self-serve desk signup yet, so an honest mailto
 *  beats a button that leads to a form that does not exist. */
const CITY_DESK_MAILTO =
  `mailto:${SUPPORT}?subject=${encodeURIComponent("Become a city desk")}` +
  `&body=${encodeURIComponent("Your town:\nYour name:\nPhone:\n")}`;

function readCookie(name) {
  try {
    const hit = document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`);
    return hit ? hit[2] : "";
  } catch {
    return "";
  }
}

/** Written to BOTH stores. localStorage is the one we read first; the cookie survives the cases
 *  localStorage does not — Safari private mode, a browser set to clear site data on close — and a
 *  visitor asked to pick their language twice reasonably concludes the site is broken. */
function rememberLanguageChoice(code) {
  try {
    localStorage.setItem(LANG_CHOICE_KEY, code);
  } catch {
    /* storage disabled or full; the cookie below is the fallback, not a nicety */
  }
  try {
    document.cookie = `${LANG_CHOICE_KEY}=${code};path=/;max-age=31536000;samesite=lax`;
  } catch {
    /* nothing more to try; the modal will ask again next visit, which is the safe failure */
  }
}

function storedLanguageChoice() {
  let value = "";
  try {
    value = localStorage.getItem(LANG_CHOICE_KEY) || "";
  } catch {
    value = "";
  }
  if (!value) value = readCookie(LANG_CHOICE_KEY);
  return value === "hi" || value === "en" ? value : "";
}

function readCachedCities() {
  try {
    const raw = localStorage.getItem(CITY_CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** The six benefit cards (design §4). `tint` alternates the reference's white / mint / amber steps
 *  so the grid is not one flat colour; the copy lives in i18n (`platform.benefit<Key>*`). */
const BENEFITS = [
  { key: "OneAddress", Icon: Home, tint: "white" },
  { key: "Local", Icon: MapPin, tint: "mint" },
  { key: "Order", Icon: ShoppingCart, tint: "white" },
  { key: "Hire", Icon: Wrench, tint: "white" },
  { key: "Language", Icon: Languages, tint: "mint" },
  { key: "Free", Icon: BadgeCheck, tint: "amber" },
];

const CARD_TINT = {
  white: "bg-[var(--as-card)] border-[var(--as-border)]",
  mint: "bg-[var(--as-mint-bg)] border-[var(--as-mint-border)]",
  amber: "bg-[var(--as-amber-bg)] border-[var(--as-amber-border)]",
};
const ICON_TINT = {
  white: { background: "var(--as-live-bg)", color: "var(--as-primary)" },
  mint: { background: "#d5e9db", color: "#1c7a57" },
  amber: { background: "#f4e4bf", color: "var(--as-accent-deep)" },
};

/** A reusable pill in the landing palette. The global `Button` maps onto the per-city theme tokens
 *  (`bg-primary`/`hsl(var(--primary))`), which the root page has no tenant to supply — so the
 *  landing keeps its own `--as-*`-scoped pill rather than pulling the wrong colours. */
function Pill({ as = "button", variant = "primary", className = "", ...props }) {
  const base =
    "inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full px-[26px] py-3.5 text-[16px] font-semibold transition-[background-color,box-shadow,transform,border-color] duration-150 active:scale-[.98]";
  const styles = {
    primary:
      "bg-[var(--as-primary)] text-white shadow-[var(--as-shadow-sm)] hover:bg-[var(--as-primary-ink)] hover:shadow-[var(--as-shadow-md)]",
    outline:
      "border-[1.5px] border-[var(--as-ink)] text-[var(--as-ink)] hover:bg-[var(--as-paper-2)]",
    onDark: "bg-[var(--as-on-dark)] text-[var(--as-ink)] hover:bg-white",
    outlineOnDark:
      "border-[1.5px] border-[#86c3a4] text-[var(--as-on-dark)] hover:bg-[rgba(255,255,255,0.08)]",
  };
  const Tag = as;
  return <Tag className={`${base} ${styles[variant]} ${className}`} {...props} />;
}

/**
 * `apnashahar.online` with no city in the path — the platform's own front door.
 *
 * **The city picker is the spine of this page, not a band within it.** Nobody — resident, shop
 * owner or worker — can do anything until they have picked a town, so the picker sits inside the
 * hero and is reachable without scrolling. Everything below is the marketing case for the platform;
 * the "Find your city" CTAs all lead back to the picker, and "Get the app" leads to the honest
 * coming-soon apps strip rather than a store link that would 404.
 *
 * **Light green-anchored, and that is a usability decision before an aesthetic one.** The audience
 * reads this on a low-nit LCD in daylight, where a dark ground glares; a light "printed" surface
 * reads as official to a first-time, low-trust visitor. Palette + fonts in `index.css` under
 * `.as-platform`, scoped so they can never collide with the per-city `--city-*` system.
 *
 * **Nothing here is fabricated.** No store buttons while the apps are unpublished, no invented user
 * counts or earnings, no testimonials. The city-desk card is explicitly a labelled *sample* with no
 * numbers. "List your shop" / "Become a city desk" are a scroll and a prefilled mailto because
 * there is no web signup to link to. In this market an inflated number is a scam signal.
 */
export default function PlatformHome() {
  const { tenants, lang, t, changeLanguage, booting, bootError, retryBoot } = useApp();
  const [cityQuery, setCityQuery] = useState("");
  const [askLanguage, setAskLanguage] = useState(false);
  const [cachedCities, setCachedCities] = useState(() => readCachedCities());

  // Ask once, ever. `AppContext` already renders in *some* language, so this is not about picking
  // one — it is about the visitor having been given the choice, which in a Hindi-first market is
  // itself the trust signal. A returning visitor must never see it again.
  useEffect(() => {
    if (!storedLanguageChoice()) setAskLanguage(true);
  }, []);

  const chooseLanguage = useCallback(
    (code) => {
      rememberLanguageChoice(code);
      setAskLanguage(false);
      // `changeLanguage` loads the language chunk, sets `<html lang>` and re-renders. Fire and
      // forget: it catches its own failure and leaves the current language in place.
      changeLanguage(code);
    },
    [changeLanguage],
  );

  useEffect(() => {
    const title = t("platform.metaTitle");
    const description = t("platform.metaDescription");
    document.title = title;

    const upsertMeta = (selector, attrs) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs).forEach(([k, v]) => {
          if (k !== "content") el.setAttribute(k, v);
        });
        document.head.appendChild(el);
      }
      el.setAttribute("content", attrs.content);
    };

    upsertMeta('meta[name="description"]', { name: "description", content: description });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: title });
    upsertMeta('meta[property="og:description"]', {
      property: "og:description",
      content: description,
    });
    upsertMeta('meta[property="og:image"]', {
      property: "og:image",
      content: `${window.location.origin}/icons/icon-512.png`,
    });
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
  }, [t]);

  // Cache the last good list. Only ever written from a REAL success, so a failed load can never
  // overwrite a good cache with nothing.
  useEffect(() => {
    if (booting || bootError || tenants.length === 0) return;
    try {
      localStorage.setItem(CITY_CACHE_KEY, JSON.stringify(tenants));
    } catch {
      /* a full quota costs us the offline nicety and nothing else */
    }
    setCachedCities(tenants);
  }, [tenants, booting, bootError]);

  /**
   * **BUG-001, and the whole reason this is a discriminated state.** The old page rendered
   * `tenants.length` unconditionally, so a failed `/api/tenants` — an empty array that never got
   * filled — displayed "0 cities online". A visitor was told the platform has no cities when the
   * truth was that we could not reach the server. Unknown is not zero, and the count strip is now
   * rendered from `ready` alone.
   */
  const status = useMemo(() => {
    if (booting) return "loading";
    if (bootError) return cachedCities.length > 0 ? "stale" : "error";
    if (tenants.length === 0) return "empty";
    return "ready";
  }, [booting, bootError, tenants.length, cachedCities.length]);

  const shownCities = status === "stale" ? cachedCities : tenants;
  const liveCity = shownCities[0] || null;
  const liveCityName = liveCity ? localized(lang, liveCity, "name") : "";
  const cityOnline = useCityOnline();

  const filteredCities = useMemo(() => {
    const q = cityQuery.trim().toLowerCase();
    if (!q) return shownCities;
    return shownCities.filter((city) => {
      const name = localized(lang, city, "name").toLowerCase();
      return name.includes(q) || (city.slug || "").toLowerCase().includes(q);
    });
  }, [shownCities, lang, cityQuery]);

  const open = (slug) => {
    localStorage.setItem(TENANT_KEY, slug);
    window.location.assign(`/${slug}`);
  };

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      block: "start",
    });
  };
  const scrollToPicker = () => scrollTo("picker");

  // `/#apps` is where the city site's "Business app" links land (JoinWorker, the worker note on
  // Account, app-only notifications). The browser cannot scroll to an anchor that renders after
  // boot, so do it once the page is up.
  useEffect(() => {
    if (window.location.hash !== "#apps") return undefined;
    const timer = setTimeout(() => document.getElementById("apps")?.scrollIntoView?.({ block: "start" }), 0);
    return () => clearTimeout(timer);
  }, []);

  // Policy pages live inside the per-city router (`/<city>/privacy`); the root has none, so the
  // footer links to a live city's copy. Omitted entirely when no city has loaded — never a dead link.
  const policyBase = liveCity ? `/${liveCity.slug}` : null;

  return (
    <div className="as-platform min-h-screen" data-testid="platform-home">
      {askLanguage && <LanguageModal t={t} onChoose={chooseLanguage} />}

      <a
        href="#picker"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-xl focus:bg-[var(--as-card)] focus:px-4 focus:py-3 focus:font-semibold"
      >
        {t("platform.findCity")}
      </a>

      {/* ---------------------------------------------------------------- header */}
      <header
        className="sticky top-0 z-40 border-b border-[var(--as-border)] backdrop-blur-[12px]"
        style={{ background: "rgba(243,246,238,.82)" }}
      >
        <div className="mx-auto flex h-16 max-w-[1180px] items-center justify-between px-[22px]">
          <AsWordmark />
          <nav className="flex items-center gap-3 sm:gap-5" aria-label={t("platform.navLabel")}>
            <a
              href="#picker"
              className="hidden text-[15px] font-medium text-[var(--as-ink-2)] transition-colors duration-150 hover:text-[var(--as-primary)] sm:inline"
            >
              {t("platform.navSeeCities")}
            </a>
            <a
              href="#for-shops"
              className="hidden text-[15px] font-medium text-[var(--as-ink-2)] transition-colors duration-150 hover:text-[var(--as-primary)] sm:inline"
            >
              {t("platform.navForShops")}
            </a>
            <LanguageToggle lang={lang} t={t} onChoose={chooseLanguage} />
            <button
              type="button"
              onClick={scrollToPicker}
              data-testid="platform-nav-find"
              className="hidden rounded-full bg-[var(--as-primary)] px-[18px] py-2.5 text-[15px] font-semibold text-white transition-colors duration-150 hover:bg-[var(--as-primary-ink)] sm:inline-block"
            >
              {t("platform.findCity")}
            </button>
          </nav>
        </div>
      </header>

      <main>
      {/* ------------------------------------------------- hero + city picker */}
      <section
        className="mx-auto max-w-[1180px] px-[22px] pb-14 pt-[44px]"
        data-testid="platform-hero"
        aria-labelledby="hero-title"
      >
        <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_.95fr]">
          <div>
            <span className="as-eyebrow as-reveal inline-block rounded-full bg-[var(--as-live-bg)] px-3.5 py-1.5 !text-[var(--as-primary)]">
              {t("platform.heroEyebrow")}
            </span>
            <h1
              id="hero-title"
              className="as-reveal mt-5 font-display text-[clamp(2.5rem,6vw,4.4rem)] font-extrabold"
              style={{ animationDelay: "40ms" }}
            >
              {t("platform.heroTitleLead")}{" "}
              <span className="text-[var(--as-primary)]">{t("platform.heroTitleAccent")}</span>
            </h1>
            <p
              className="as-reveal mt-[18px] max-w-[32ch] text-[clamp(1.05rem,2.4vw,1.2rem)] leading-relaxed text-[var(--as-ink-2)]"
              style={{ animationDelay: "80ms" }}
            >
              {t("platform.heroLede")}
            </p>
            <div
              className="as-reveal mt-7 flex flex-col gap-3 sm:flex-row sm:items-center"
              style={{ animationDelay: "120ms" }}
            >
              <Pill onClick={scrollToPicker} data-testid="platform-hero-find">
                {t("platform.findCity")}
              </Pill>
              <Pill variant="outline" onClick={() => scrollTo("apps")} data-testid="platform-hero-app">
                {t("platform.getApp")}
              </Pill>
            </div>
            {liveCity && (
              <p
                className="as-reveal mt-5 flex items-center gap-2.5 text-[14px] text-[var(--as-ink-3)]"
                style={{ animationDelay: "150ms" }}
                data-testid="platform-hero-live"
              >
                <span className="as-live-dot inline-block h-2 w-2 rounded-full bg-[var(--as-primary)]" />
                {t("platform.heroLive", { city: liveCityName })}
              </p>
            )}

            <div id="picker" className="as-reveal mt-8 max-w-[460px] scroll-mt-24" style={{ animationDelay: "180ms" }}>
              <CityCombobox
                t={t}
                lang={lang}
                status={status}
                cities={shownCities}
                filtered={filteredCities}
                query={cityQuery}
                onQuery={setCityQuery}
                onPick={open}
                onRetry={retryBoot}
                addCityHref={ADD_CITY_MAILTO}
              />
              {status === "ready" && (
                <p className="mt-3 text-[13.5px] text-[var(--as-ink-3)]" data-testid="platform-city-count">
                  {t(
                    tenants.length === 1 ? "platform.cityCountLive" : "platform.cityCountLivePlural",
                    { count: tenants.length },
                  )}
                </p>
              )}
              {status === "stale" && (
                <p className="mt-3 text-[13.5px] text-[var(--as-ink-3)]" data-testid="platform-city-cached">
                  {t("platform.citiesCached")}
                </p>
              )}
              <a
                href={ADD_CITY_MAILTO}
                data-testid="platform-not-listed"
                className="mt-3.5 inline-block border-b-[1.5px] border-[var(--as-border-2)] pb-px font-semibold transition-colors duration-150 hover:border-[var(--as-primary)]"
              >
                {t("platform.notListed")}
              </a>
            </div>
          </div>

          {/* Decorative flat-illustration town, in a rounded mint panel with a floating city chip.
              Purely decorative — aria-hidden — and drawn, never a stock photo of a town we would be
              misrepresenting. Shown beside the hero on desktop; a shorter version stacks on mobile. */}
          <div className="relative hidden h-[470px] overflow-hidden rounded-[28px] lg:block" aria-hidden="true">
            <TownScene />
            {liveCity && (
              <div className="absolute bottom-6 left-6 flex items-center gap-2.5 rounded-[14px] bg-white px-4 py-3 shadow-[var(--as-shadow-md)]">
                <AsLogo size={30} className="text-[var(--as-primary)]" />
                <div className="leading-tight">
                  <CityWordmark name={liveCityName} nameHi={liveCity.name_hi} className="text-[14px]" />
                  <div className="text-[11px] text-[var(--as-ink-4)]">{t("platform.chipTagline")}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Mobile: the town panel stacks below the picker rather than beside it. */}
        <div
          className="relative mt-8 h-[200px] overflow-hidden rounded-[20px] lg:hidden"
          aria-hidden="true"
        >
          <TownScene mobile />
        </div>
      </section>

      {/* ----------------------------------------------------------- cities strip */}
      <section
        className="bg-[var(--as-dark)]"
        aria-label={t("platform.stripEyebrow")}
        data-testid="platform-cities-strip"
      >
        <div className="mx-auto flex max-w-[1180px] flex-wrap items-center gap-x-9 gap-y-3 px-[22px] py-6">
          <span className="text-[13px] font-semibold uppercase tracking-[0.12em] text-[var(--as-on-dark-3)]">
            {t("platform.stripEyebrow")}
          </span>
          <div className="flex flex-wrap items-center gap-x-7 gap-y-2">
            {shownCities.slice(0, 4).map((city) => (
              <span
                key={city.slug}
                data-testid={`platform-strip-city-${city.slug}`}
                className="font-display text-[20px] font-bold text-[var(--as-on-dark)]"
              >
                {cityOnline(localized(lang, city, "name"), city.name_hi)}
              </span>
            ))}
            <span className="text-[14px] text-[var(--as-on-dark-3)]">{t("platform.stripNext")}</span>
          </div>
        </div>
      </section>

      {/* --------------------------------------------------------- why / benefits */}
      <section className="mx-auto max-w-[1180px] px-[22px] py-[76px]">
        <div className="max-w-[640px]">
          <p className="as-eyebrow">{t("platform.benefitsEyebrow")}</p>
          <h2 className="mt-2.5 font-display text-[clamp(1.75rem,4vw,2.6rem)] font-bold">
            {t("platform.benefitsTitle")}
          </h2>
          <p className="mt-3 text-[18px] leading-relaxed text-[var(--as-ink-2)]">
            {t("platform.benefitsSub")}
          </p>
        </div>
        <div className="mt-11 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {BENEFITS.map(({ key, Icon, tint }, i) => (
            <div
              key={key}
              style={{ animationDelay: `${i * 60}ms` }}
              className={`as-reveal flex flex-col gap-3.5 rounded-[20px] border p-7 ${CARD_TINT[tint]}`}
            >
              <span
                className="grid h-[52px] w-[52px] place-items-center rounded-[14px]"
                style={ICON_TINT[tint]}
              >
                <Icon size={26} strokeWidth={1.8} aria-hidden="true" />
              </span>
              <h3 className="font-display text-[21px] font-bold">
                {t(`platform.benefit${key}Title`)}
              </h3>
              <p className="text-[15px] leading-relaxed text-[var(--as-ink-3)]">
                {t(`platform.benefit${key}Body`)}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ------------------------------------------------------- for shop owners */}
      <section id="for-shops" className="bg-[var(--as-shop-band)] py-[76px]">
        <div className="mx-auto grid max-w-[1180px] items-center gap-14 px-[22px] lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <p className="as-eyebrow">{t("platform.shopsEyebrow")}</p>
            <h2 className="mt-2.5 font-display text-[clamp(1.75rem,4vw,2.6rem)] font-bold">
              {t("platform.shopsTitle")}
            </h2>
            <p className="mt-3.5 text-[18px] leading-relaxed text-[var(--as-ink-2)]">
              {t("platform.shopsBody")}
            </p>
            <ul className="mt-5 flex flex-col gap-3 p-0">
              {["Tick1", "Tick2", "Tick3"].map((k) => (
                <li key={k} className="flex list-none items-center gap-3">
                  <span className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-full bg-[var(--as-primary)] text-white">
                    <Check size={13} strokeWidth={3} aria-hidden="true" />
                  </span>
                  <span className="text-[16px] text-[var(--as-ink-2)]">{t(`platform.shops${k}`)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-7 flex flex-wrap items-center gap-4">
              <Pill onClick={scrollToPicker} data-testid="platform-cta-owners">
                {t("platform.shopsCtaList")}
              </Pill>
              <a
                href="#for-shops"
                className="font-semibold text-[var(--as-primary)] hover:text-[var(--as-primary-ink)]"
              >
                {t("platform.shopsCtaPlans")}
              </a>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            {/* FREE */}
            <div className="flex flex-col gap-2 rounded-[18px] border border-[var(--as-border)] bg-[var(--as-card)] p-6">
              <span className="text-[13px] font-semibold text-[var(--as-ink-4)]">
                {t("platform.planFreeName")}
              </span>
              <div className="font-display text-[30px] font-extrabold text-[var(--as-ink)]">
                {t("platform.planFreePrice")}
              </div>
              <p className="text-[13px] leading-relaxed text-[var(--as-ink-3)]">
                {t("platform.planFreeBody")}
              </p>
            </div>
            {/* GROWTH (popular) */}
            <div className="relative flex flex-col gap-2 rounded-[18px] bg-[var(--as-dark)] p-6">
              <span className="absolute right-4 top-4 rounded-full bg-[var(--as-primary-bright)] px-2 py-1 text-[10px] font-bold text-[#0c1a13]">
                {t("platform.planPopular")}
              </span>
              <span className="text-[13px] font-semibold text-[var(--as-on-dark-3)]">
                {t("platform.planGrowthName")}
              </span>
              <div className="font-display text-[30px] font-extrabold text-[var(--as-on-dark)]">
                {t("platform.planGrowthPrice")}
                <span className="text-[14px] font-medium text-[var(--as-on-dark-3)]">
                  {t("platform.planPerMonth")}
                </span>
              </div>
              <p className="text-[13px] leading-relaxed text-[var(--as-on-dark-2)]">
                {t("platform.planGrowthBody")}
              </p>
            </div>
            {/* PREMIUM */}
            <div className="flex flex-col gap-2 rounded-[18px] border border-[var(--as-amber-border)] bg-[var(--as-amber-bg)] p-6">
              <span className="text-[13px] font-semibold text-[var(--as-accent-deep)]">
                {t("platform.planPremiumName")}
              </span>
              <div className="font-display text-[30px] font-extrabold text-[var(--as-ink)]">
                {t("platform.planPremiumPrice")}
                <span className="text-[14px] font-medium text-[var(--as-ink-4)]">
                  {t("platform.planPerMonth")}
                </span>
              </div>
              <p className="text-[13px] leading-relaxed text-[var(--as-ink-3)]">
                {t("platform.planPremiumBody")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* --------------------------------------------------- how money works (trust) */}
      <section className="bg-[var(--as-dark)] py-[70px]">
        <div className="mx-auto grid max-w-[1180px] items-start gap-12 px-[22px] lg:grid-cols-3">
          <div>
            <p className="text-[13px] font-semibold uppercase tracking-[0.14em] text-[var(--as-on-dark-accent)]">
              {t("platform.moneyEyebrow")}
            </p>
            <h2 className="mt-3.5 font-display text-[clamp(1.6rem,3.4vw,2.35rem)] font-bold leading-tight text-[var(--as-on-dark)]">
              {t("platform.moneyTitle")}
            </h2>
          </div>
          {[
            { k: "Pay", Icon: ShieldCheck, iconBg: "#1c7a57", iconColor: "#fff" },
            { k: "Person", Icon: Users, iconBg: "var(--as-accent)", iconColor: "#12261b" },
          ].map(({ k, Icon, iconBg, iconColor }) => (
            <div key={k} className="flex flex-col gap-3">
              <span
                className="grid h-[46px] w-[46px] place-items-center rounded-xl"
                style={{ background: iconBg, color: iconColor }}
              >
                <Icon size={24} strokeWidth={1.8} aria-hidden="true" />
              </span>
              <h3 className="font-display text-[19px] font-bold text-[var(--as-on-dark)]">
                {t(`platform.money${k}Title`)}
              </h3>
              <p className="text-[15px] leading-relaxed text-[var(--as-on-dark-2)]">
                {t(`platform.money${k}Body`)}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* -------------------------------------------------------- for city desks */}
      <section className="mx-auto max-w-[1180px] px-[22px] py-[76px]">
        <div className="grid items-center gap-14 lg:grid-cols-[1.1fr_0.9fr]">
          <div>
            <p className="as-eyebrow">{t("platform.desksEyebrow")}</p>
            <h2 className="mt-2.5 font-display text-[clamp(1.7rem,3.8vw,2.5rem)] font-bold">
              {t("platform.desksTitle")}
            </h2>
            <p className="mt-3.5 text-[18px] leading-relaxed text-[var(--as-ink-2)]">
              {t("platform.desksBody")}
            </p>
            <div className="mt-7">
              <Pill as="a" href={CITY_DESK_MAILTO} data-testid="platform-desk-cta">
                {t("platform.desksCta")}
              </Pill>
            </div>
          </div>

          {/* Illustrative — a labelled SAMPLE, with NO fabricated figures (design §7): the two
              tiles describe what the desk view shows rather than inventing a count or a rupee sum. */}
          <div
            className="flex flex-col gap-[18px] rounded-[22px] border border-[var(--as-border)] bg-[var(--as-card)] p-7"
            data-testid="platform-desk-card"
          >
            <div className="flex items-center justify-between">
              <span className="text-[13px] font-semibold uppercase tracking-[0.1em] text-[var(--as-ink-4)]">
                {t("platform.deskCardLabel")}
              </span>
              <span className="rounded-full bg-[var(--as-live-bg)] px-2.5 py-1 text-[12px] font-semibold text-[var(--as-primary)]">
                {t("platform.deskSample")}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3.5">
              <div className="rounded-[14px] bg-[var(--as-mint-bg)] p-4">
                <div className="font-display text-[15px] font-bold text-[var(--as-ink)]">
                  {t("platform.deskStat1Title")}
                </div>
                <div className="mt-1 text-[12.5px] text-[var(--as-ink-3)]">
                  {t("platform.deskStat1Note")}
                </div>
              </div>
              <div className="rounded-[14px] bg-[var(--as-amber-bg)] p-4">
                <div className="font-display text-[15px] font-bold text-[var(--as-accent-deep)]">
                  {t("platform.deskStat2Title")}
                </div>
                <div className="mt-1 text-[12.5px] text-[var(--as-ink-3)]">
                  {t("platform.deskStat2Note")}
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2.5">
              <div className="flex items-center justify-between rounded-xl bg-[var(--as-paper)] px-3.5 py-3">
                <span className="text-[14px] text-[var(--as-ink-2)]">{t("platform.deskRow1Name")}</span>
                <span className="text-[12px] font-semibold text-[var(--as-primary)]">
                  {t("platform.deskRow1Status")}
                </span>
              </div>
              <div className="flex items-center justify-between rounded-xl bg-[var(--as-paper)] px-3.5 py-3">
                <span className="text-[14px] text-[var(--as-ink-2)]">{t("platform.deskRow2Name")}</span>
                <span className="text-[12px] font-semibold text-[var(--as-danger)]">
                  {t("platform.deskRow2Status")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------- the apps */}
      {/* Kept for honesty, not fabrication: the apps are not on any store yet, so these are badges,
          not store buttons, and "Get the app" scrolls here rather than to a link that would 404. */}
      <section id="apps" className="mx-auto max-w-[1180px] scroll-mt-20 px-[22px] pb-[20px]">
        <div className="grid gap-4 sm:grid-cols-2">
          {[
            ["customer", "appCustomerName", "appCustomerFor", "text-[#127a68]"],
            ["partner", "appPartnerName", "appPartnerFor", "text-[var(--as-ink)]"],
          ].map(([id, nameKey, forKey, tint]) => (
            <div
              key={id}
              className="flex items-center gap-4 rounded-[18px] border border-[var(--as-border)] bg-[var(--as-card)] p-5 shadow-[var(--as-shadow-sm)]"
            >
              <AsLogo size={56} className={tint} />
              <div>
                <h3 className="font-display text-[17px] font-bold">{t(`platform.${nameKey}`)}</h3>
                <p className="text-sm text-[var(--as-ink-3)]">{t(`platform.${forKey}`)}</p>
                <span
                  data-testid={`platform-store-badge-${id}`}
                  className="as-online-word mt-1.5 inline-block cursor-default rounded-md bg-[#fcecd2] px-2.5 py-1 text-[11.5px] font-bold uppercase tracking-[0.06em] text-[var(--as-accent-deep)]"
                >
                  {t("platform.appComingSoon")}
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ------------------------------------------------------------ final CTA */}
      <section className="mt-[56px] bg-[var(--as-primary)] py-[84px]">
        <div className="mx-auto flex max-w-[820px] flex-col items-center gap-5 px-[22px] text-center">
          <h2 className="font-display text-[clamp(1.9rem,4.6vw,3rem)] font-extrabold leading-tight text-[var(--as-on-dark)]">
            {t("platform.finalTitle")}
          </h2>
          <p className="max-w-[560px] text-[18px] leading-relaxed text-[#cde6d5]">
            {t("platform.finalSub")}
          </p>
          <div className="mt-2 flex flex-col gap-3 sm:flex-row">
            <Pill variant="onDark" onClick={scrollToPicker} data-testid="platform-final-find">
              {t("platform.findCity")}
            </Pill>
            <Pill variant="outlineOnDark" onClick={() => scrollTo("apps")} data-testid="platform-final-app">
              {t("platform.getApp")}
            </Pill>
          </div>
        </div>
      </section>
      </main>

      {/* -------------------------------------------------------------- footer */}
      <footer className="bg-[var(--as-dark)] pb-11 pt-[52px]" data-testid="platform-footer">
        <div className="mx-auto flex max-w-[1180px] flex-wrap items-center justify-between gap-5 px-[22px]">
          <span className="flex items-center gap-2.5">
            <AsLogo size={34} className="text-[var(--as-primary-bright)]" />
            <span className="font-display text-[18px] font-extrabold text-[var(--as-on-dark)]">
              {t("brand.platform")}
            </span>
          </span>
          <nav className="flex flex-wrap items-center gap-6" aria-label={t("platform.footerNavLabel")}>
            <a href="#picker" className="text-[14px] text-[var(--as-on-dark-2)] hover:text-[var(--as-on-dark)]">
              {t("platform.navSeeCities")}
            </a>
            <a href="#for-shops" className="text-[14px] text-[var(--as-on-dark-2)] hover:text-[var(--as-on-dark)]">
              {t("platform.navForShops")}
            </a>
            {policyBase && (
              <>
                <a
                  href={`${policyBase}/privacy`}
                  className="text-[14px] text-[var(--as-on-dark-2)] hover:text-[var(--as-on-dark)]"
                >
                  {t("platform.footerPrivacy")}
                </a>
                <a
                  href={`${policyBase}/terms`}
                  className="text-[14px] text-[var(--as-on-dark-2)] hover:text-[var(--as-on-dark)]"
                >
                  {t("platform.footerTerms")}
                </a>
              </>
            )}
          </nav>
        </div>
        <div className="mx-auto mt-7 max-w-[1180px] border-t border-[#274031] px-[22px] pt-[18px]">
          <span className="text-[13px] text-[#6c8377]">
            {t("platform.footerCopy", { year: new Date().getFullYear() })}
          </span>
        </div>
      </footer>
    </div>
  );
}

/** The decorative flat-illustration town (design reference): sun, temple, water tank, three shops
 *  with green/amber awnings, a tree and a bus, on a mint ground. Purely presentational — the parent
 *  sets `aria-hidden` — so it carries no text and no title. `mobile` renders the shorter artboard. */
function TownScene({ mobile = false }) {
  if (mobile) {
    return (
      <svg
        viewBox="0 0 350 200"
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid slice"
        style={{ background: "linear-gradient(160deg,#E3F0E4,#D2E8D8)" }}
        focusable="false"
      >
        <circle cx="292" cy="44" r="28" fill="#E7B24A" />
        <polygon points="150,32 134,100 166,100" fill="#C1502B" />
        <circle cx="150" cy="26" r="6" fill="#E7B24A" />
        <rect x="0" y="142" width="350" height="58" fill="#DFE9D5" />
        <rect x="26" y="106" width="80" height="44" fill="#FFFFFF" />
        <rect x="26" y="96" width="80" height="12" fill="#16794F" />
        <rect x="36" y="120" width="26" height="30" fill="#2E5E48" />
        <rect x="118" y="90" width="88" height="60" fill="#FCFCF6" />
        <rect x="118" y="80" width="88" height="12" fill="#1C7A57" />
        <rect x="150" y="114" width="34" height="36" fill="#2E5E48" />
        <rect x="220" y="104" width="86" height="46" fill="#FFFFFF" />
        <rect x="220" y="94" width="86" height="12" fill="#E7B24A" />
        <rect x="232" y="120" width="26" height="30" fill="#2E5E48" />
      </svg>
    );
  }
  return (
    <svg
      viewBox="0 0 560 470"
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMid slice"
      style={{ background: "linear-gradient(160deg,#E3F0E4 0%,#D2E8D8 100%)" }}
      focusable="false"
    >
      <circle cx="452" cy="92" r="44" fill="#E7B24A" />
      <g>
        <rect x="72" y="150" width="10" height="118" fill="#2E5E48" />
        <rect x="100" y="150" width="10" height="118" fill="#2E5E48" />
        <ellipse cx="91" cy="150" rx="42" ry="19" fill="#264F3C" />
        <rect x="49" y="122" width="84" height="32" rx="8" fill="#2E5E48" />
      </g>
      <g>
        <polygon points="238,66 214,176 262,176" fill="#C1502B" />
        <polygon points="238,66 238,176 262,176" fill="#A8401F" />
        <circle cx="238" cy="57" r="9" fill="#E7B24A" />
      </g>
      <rect x="0" y="326" width="560" height="144" fill="#DFE9D5" />
      <rect x="0" y="326" width="560" height="14" fill="#C7D8B9" />
      <g>
        <rect x="40" y="236" width="120" height="90" fill="#FFFFFF" />
        <rect x="40" y="220" width="120" height="20" fill="#16794F" />
        <rect x="52" y="260" width="44" height="66" fill="#2E5E48" />
        <rect x="108" y="260" width="40" height="32" fill="#CFE3DA" />
        <g fill="#2E9B6E">
          <rect x="40" y="240" width="20" height="10" />
          <rect x="80" y="240" width="20" height="10" />
          <rect x="120" y="240" width="20" height="10" />
        </g>
      </g>
      <g>
        <rect x="176" y="194" width="130" height="132" fill="#FCFCF6" />
        <rect x="176" y="178" width="130" height="20" fill="#1C7A57" />
        <rect x="190" y="214" width="44" height="40" fill="#DCEBDF" />
        <rect x="248" y="214" width="44" height="40" fill="#DCEBDF" />
        <rect x="206" y="274" width="70" height="52" fill="#2E5E48" />
      </g>
      <g>
        <rect x="322" y="228" width="126" height="98" fill="#FFFFFF" />
        <rect x="322" y="212" width="126" height="20" fill="#E7B24A" />
        <rect x="336" y="250" width="46" height="76" fill="#2E5E48" />
        <rect x="396" y="250" width="40" height="34" fill="#CFE3DA" />
        <g fill="#16794F">
          <rect x="322" y="232" width="21" height="10" />
          <rect x="364" y="232" width="21" height="10" />
          <rect x="406" y="232" width="21" height="10" />
        </g>
      </g>
      <g transform="translate(452,284)">
        <circle cx="8" cy="40" r="15" fill="#14251B" />
        <circle cx="8" cy="40" r="6" fill="#DFE9D5" />
        <circle cx="62" cy="40" r="15" fill="#14251B" />
        <circle cx="62" cy="40" r="6" fill="#DFE9D5" />
        <path
          d="M8 40 L34 40 L44 20 L58 20 L62 40"
          fill="none"
          stroke="#16794F"
          strokeWidth="7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <rect x="24" y="22" width="20" height="14" rx="3" fill="#2E9B6E" />
      </g>
      <g transform="translate(300,248)">
        <rect x="18" y="46" width="8" height="34" fill="#8A5A34" />
        <circle cx="22" cy="40" r="24" fill="#1C7A57" />
        <circle cx="8" cy="48" r="16" fill="#176046" />
        <circle cx="36" cy="48" r="16" fill="#176046" />
      </g>
    </svg>
  );
}

/**
 * The city picker: one search field, and a results panel that OVERLAYS rather than pushes.
 *
 * **Why this is a combobox and not the list it started as.** Every city was rendered inline as a
 * 64px card, so a platform with 171 towns put roughly eleven thousand pixels between the hero and
 * everything below it. The panel is absolutely positioned and capped at 44vh, so the page is the
 * same height whether one town is live or a thousand are.
 *
 * **The top match still shows as a card when the panel is closed** — "a real town is already
 * online" is the strongest honest trust signal this page has. Keyboard behaviour is the real ARIA
 * combobox pattern: arrows move, Enter picks, Escape closes, `aria-activedescendant` follows.
 */
function CityCombobox({ t, lang, status, cities, filtered, query, onQuery, onPick, onRetry, addCityHref }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    listRef.current.querySelector(`[data-idx="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  const ready = status === "ready" || status === "stale";
  const noMatch = ready && query.trim() !== "" && filtered.length === 0;
  const top = filtered[0];

  const pick = (city) => {
    if (!city) return;
    setOpen(false);
    onPick(city.slug);
  };

  const onKeyDown = (e) => {
    if (!ready) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      const step = e.key === "ArrowDown" ? 1 : -1;
      setActive((i) => Math.min(Math.max(i + step, 0), Math.max(filtered.length - 1, 0)));
    } else if (e.key === "Enter") {
      if (open && filtered[active]) {
        e.preventDefault();
        pick(filtered[active]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={boxRef} className="relative">
      <div className="flex flex-wrap items-center gap-2.5 rounded-[14px] border-[1.5px] border-[var(--as-border-2)] bg-[var(--as-card)] p-1 pl-4 shadow-[var(--as-shadow-sm)] focus-within:border-[var(--as-primary)] focus-within:shadow-[0_0_0_4px_rgba(22,121,79,.12)]">
        <Search size={22} strokeWidth={1.9} className="text-[var(--as-ink-3)]" aria-hidden="true" />
        <input
          type="text"
          role="combobox"
          autoComplete="off"
          aria-expanded={open}
          aria-controls="city-listbox"
          aria-autocomplete="list"
          aria-activedescendant={open && filtered[active] ? `city-opt-${active}` : undefined}
          value={query}
          onChange={(e) => {
            onQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          aria-label={t("platform.searchLabel")}
          placeholder={t("platform.searchPlaceholder")}
          data-testid="platform-city-search"
          className="min-w-0 flex-1 border-0 bg-transparent py-3 text-base text-[var(--as-ink)] outline-none placeholder:text-[var(--as-ink-3)]"
        />
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label={t("platform.cityShowAll")}
          data-testid="platform-find-btn"
          className="min-h-[48px] rounded-xl bg-[var(--as-primary)] px-[18px] py-3 font-semibold text-white shadow-[var(--as-shadow-sm)] transition-[background-color,box-shadow,transform] duration-150 hover:bg-[var(--as-primary-ink)] hover:shadow-[var(--as-shadow-md)] active:scale-[.98] max-[430px]:w-full"
        >
          {t("platform.findCta")}
        </button>
      </div>

      {open && ready && (
        <div
          className="absolute left-0 right-0 top-full z-30 mt-2 overflow-hidden rounded-2xl border border-[var(--as-border-2)] bg-[var(--as-card)] shadow-[var(--as-shadow-lg)]"
          data-testid="platform-city-panel"
        >
          <ul
            id="city-listbox"
            ref={listRef}
            role="listbox"
            aria-label={t("platform.cityListLabel")}
            className="max-h-[44vh] overflow-y-auto overscroll-contain p-1"
          >
            {filtered.map((city, i) => (
              <li key={city.slug} role="none">
                <button
                  type="button"
                  role="option"
                  id={`city-opt-${i}`}
                  data-idx={i}
                  aria-selected={i === active}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => pick(city)}
                  data-testid={`platform-city-${city.slug}`}
                  className={`flex min-h-[56px] w-full items-center gap-3 rounded-xl p-2.5 text-left transition-colors duration-100 ${
                    i === active ? "bg-[var(--as-paper-2)]" : ""
                  }`}
                >
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-[var(--as-primary)] font-display text-[17px] font-extrabold text-white">
                    {cityInitial(localized(lang, city, "name"))}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-display text-[16px] font-bold">
                      {localized(lang, city, "name")}
                    </span>
                    <span className="block truncate text-[12.5px] text-[var(--as-ink-3)]">
                      {localized(lang, city, "tagline") || `apnashahar.online/${city.slug}`}
                    </span>
                  </span>
                  <ChevronRight size={16} className="text-[var(--as-ink-3)]" aria-hidden="true" />
                </button>
              </li>
            ))}

            {noMatch && (
              <li role="none" className="p-3" data-testid="platform-city-nomatch">
                <p className="font-display font-bold">{t("platform.noMatchTitle")}</p>
                <a href={addCityHref} className="mt-1.5 inline-block font-semibold underline">
                  {t("platform.noMatchCta")}
                </a>
              </li>
            )}
          </ul>

          {filtered.length > 0 && filtered.length < cities.length && (
            <p className="border-t border-[var(--as-border)] px-3 py-2 text-[12px] text-[var(--as-ink-3)]">
              {t("platform.cityResultCount", { count: filtered.length, total: cities.length })}
            </p>
          )}
        </div>
      )}

      {!open && ready && top && (
        <button
          type="button"
          onClick={() => pick(top)}
          data-testid={`platform-city-${top.slug}`}
          className="mt-3 flex min-h-[64px] w-full items-center gap-3.5 rounded-2xl border-[1.5px] border-[var(--as-border-2)] bg-[var(--as-card)] p-4 text-left transition-[transform,box-shadow,border-color] duration-150 hover:-translate-y-0.5 hover:border-[var(--as-primary)] hover:shadow-[var(--as-shadow-md)]"
        >
          <span className="grid h-[46px] w-[46px] shrink-0 place-items-center rounded-xl bg-[var(--as-primary)] font-display text-[22px] font-extrabold text-white">
            {cityInitial(localized(lang, top, "name"))}
          </span>
          <span className="min-w-0">
            <span className="block font-display text-[19px] font-bold">
              {localized(lang, top, "name")}
            </span>
            <span className="block truncate text-[13.5px] text-[var(--as-ink-3)]">
              {localized(lang, top, "tagline")}
            </span>
          </span>
          <span className="ml-auto flex items-center gap-1.5">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--as-live-bg)] px-2.5 py-1 text-xs font-bold text-[var(--as-live-ink)]">
              <span className="as-live-dot h-[7px] w-[7px] rounded-full bg-[var(--as-live-ink)]" />
              {t("platform.live")}
            </span>
            <ChevronRight size={18} className="text-[var(--as-ink-3)]" aria-hidden="true" />
          </span>
        </button>
      )}

      {!open && noMatch && (
        <div
          data-testid="platform-city-nomatch"
          className="mt-3 rounded-[14px] border border-dashed border-[var(--as-border-2)] bg-[var(--as-paper-2)] p-4"
        >
          <p className="font-display font-bold">{t("platform.noMatchTitle")}</p>
          <a href={addCityHref} className="mt-2 inline-block font-semibold underline">
            {t("platform.noMatchCta")}
          </a>
        </div>
      )}

      <div aria-live="polite" aria-busy={status === "loading"} data-testid="platform-city-region">
        {status === "loading" && <CitySkeleton t={t} />}

        {status === "error" && (
          <div
            data-testid="platform-cities-error"
            className="mt-3 rounded-2xl border border-[var(--as-border-2)] bg-[var(--as-card)] p-4"
          >
            <p className="flex items-center gap-2 font-semibold text-[var(--as-ink)]">
              <AlertTriangle size={18} className="text-[var(--as-danger)]" aria-hidden="true" />
              {t("platform.citiesErrorTitle")}
            </p>
            <p className="mt-1 text-sm text-[var(--as-ink-2)]">{t("platform.citiesErrorBody")}</p>
            <button
              type="button"
              onClick={onRetry}
              data-testid="platform-cities-retry"
              className="mt-3 min-h-[48px] rounded-xl border-[1.5px] border-[var(--as-border-2)] px-5 py-3 font-semibold text-[var(--as-ink)] transition-colors duration-150 hover:border-[var(--as-ink-3)] hover:bg-white"
            >
              {t("platform.citiesRetry")}
            </button>
          </div>
        )}

        {status === "empty" && (
          <div
            data-testid="platform-cities-empty"
            className="mt-3 rounded-2xl border border-dashed border-[var(--as-border-2)] bg-[var(--as-paper-2)] p-4"
          >
            <p className="font-display font-bold">{t("platform.citiesEmptyTitle")}</p>
            <p className="mt-1 text-sm text-[var(--as-ink-2)]">{t("platform.citiesEmptyBody")}</p>
            <a href={addCityHref} className="mt-2 inline-block font-semibold underline">
              {t("platform.noMatchCta")}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

/** A shaped placeholder, not a spinner on a blank panel: the visitor sees the city card's real
 *  outline arriving, which reads as "nearly there" rather than "nothing here". */
function CitySkeleton({ t }) {
  return (
    <div
      data-testid="platform-cities-loading"
      className="mt-3 flex min-h-[64px] w-full items-center gap-3.5 rounded-2xl border-[1.5px] border-[var(--as-border-2)] bg-[var(--as-card)] p-4"
    >
      <span className="h-[46px] w-[46px] shrink-0 animate-pulse rounded-xl bg-[var(--as-paper-2)] motion-reduce:animate-none" />
      <span className="flex-1">
        <span className="block h-4 w-28 animate-pulse rounded bg-[var(--as-paper-2)] motion-reduce:animate-none" />
        <span className="mt-2 block h-3 w-20 animate-pulse rounded bg-[var(--as-paper-2)] motion-reduce:animate-none" />
      </span>
      <span className="sr-only">{t("platform.citiesLoading")}</span>
    </div>
  );
}

/** Two ≥44px pills rather than a buried setting. In a Hindi-first market the language control IS a
 *  trust signal, so it stays visible in the header at every breakpoint. */
function LanguageToggle({ lang, t, onChoose }) {
  return (
    <div
      role="group"
      aria-label={t("platform.languageGroupLabel")}
      className="inline-flex rounded-full border border-[var(--as-border-2)] bg-[var(--as-card)] p-[3px]"
    >
      {[
        ["en", "EN"],
        ["hi", "हिं"],
      ].map(([code, label]) => (
        <button
          key={code}
          type="button"
          aria-pressed={lang === code}
          data-testid={`platform-lang-${code}`}
          onClick={() => onChoose(code)}
          className={`flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full px-3.5 py-2 text-sm font-semibold transition-[background-color,color,box-shadow] duration-150 ${
            lang === code
              ? "bg-[var(--as-primary)] text-white shadow-[var(--as-shadow-sm)]"
              : "text-[var(--as-ink-2)]"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/**
 * Shown once, on a first visit only.
 *
 * Both languages appear in their OWN script — a visitor who cannot read English must still be able
 * to find "हिंदी" — and the prompt is bilingual for the same reason. `navigator.language` only
 * decides which the browser was already rendering; it never decides FOR the visitor, because a
 * shared Android handset frequently carries someone else's locale.
 */
function LanguageModal({ t, onChoose }) {
  useEffect(() => {
    const onKey = (e) => {
      // No dismiss-without-choosing: Escape picking a language silently would leave the visitor
      // with a default they never agreed to, which is the thing this modal exists to avoid.
      if (e.key === "Tab") return;
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("platform.langModalTitle")}
      data-testid="platform-language-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-[22px] backdrop-blur-[3px]"
      style={{ background: "rgba(18,38,27,.55)" }}
    >
      <div className="w-full max-w-[420px] rounded-[22px] border border-[var(--as-border)] bg-[var(--as-card)] p-8 text-center shadow-[var(--as-shadow-lg)]">
        <AsLogo size={58} className="mx-auto mb-4 text-[var(--as-primary)]" title="Apna Shahar" />
        <h2 className="font-display text-[22px] font-extrabold">{t("platform.langModalTitle")}</h2>
        <p className="mt-1 text-[19px] text-[var(--as-ink-2)]">{t("platform.langModalTitleHi")}</p>
        <p className="mb-[22px] mt-3 text-[13.5px] text-[var(--as-ink-3)]">
          {t("platform.langModalNote")}
        </p>
        <div className="grid grid-cols-2 gap-3">
          {[
            ["en", "langEnglish", "langEnglishSub"],
            ["hi", "langHindi", "langHindiSub"],
          ].map(([code, bigKey, subKey]) => (
            <button
              key={code}
              type="button"
              autoFocus={code === "en"}
              data-testid={`platform-language-pick-${code}`}
              onClick={() => onChoose(code)}
              className="min-h-[48px] rounded-[14px] border-[1.5px] border-[var(--as-border-2)] bg-[var(--as-paper)] px-3 py-4 transition-[transform,border-color,background-color] duration-150 hover:-translate-y-0.5 hover:border-[var(--as-primary)] hover:bg-white"
            >
              <span className="block font-display text-xl font-extrabold text-[var(--as-ink)]">
                {t(`platform.${bigKey}`)}
              </span>
              <span className="mt-0.5 block text-xs text-[var(--as-ink-3)]">
                {t(`platform.${subKey}`)}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
